import { useEffect, useState } from "react";
import { toApiError } from "./api/classifyFailure";
import type { Backend, ServerSettings, TaskApi } from "./api/taskApi";
import { LoginScreen } from "./components/LoginScreen";
import { WorkspaceScreen } from "./components/WorkspaceScreen";

type Phase =
  | { name: "starting" }
  | { name: "failed"; message: string }
  | { name: "signed-out"; saved: ServerSettings | undefined }
  | { name: "signed-in"; api: TaskApi; server: ServerSettings | undefined };

export function App({ backend }: { backend: Backend }) {
  const [phase, setPhase] = useState<Phase>({ name: "starting" });

  useEffect(() => {
    const lifecycle = { cancelled: false };
    void (async () => {
      try {
        const saved = await backend.savedServer();
        const resumed = await backend.resume();
        if (lifecycle.cancelled) return;
        setPhase(
          resumed === undefined
            ? { name: "signed-out", saved }
            : { name: "signed-in", api: resumed, server: saved },
        );
      } catch (failure) {
        if (!lifecycle.cancelled) setPhase({ name: "failed", message: toApiError(failure).message });
      }
    })();
    return () => {
      lifecycle.cancelled = true;
    };
  }, [backend]);

  if (phase.name === "starting") return <div className="boot">Starting…</div>;
  if (phase.name === "failed") {
    return (
      <div className="boot" role="alert">
        td could not start: {phase.message}
      </div>
    );
  }

  if (phase.name === "signed-out") {
    return (
      <LoginScreen
        backend={backend}
        saved={phase.saved}
        onSignedIn={(api) => {
          void backend.savedServer().then((server) => setPhase({ name: "signed-in", api, server }));
        }}
      />
    );
  }

  const server = phase.server;
  return (
    <WorkspaceScreen
      api={phase.api}
      backend={backend}
      serverLabel={server === undefined ? "your server" : `${server.serverUrl} as ${server.username}`}
      onSignedOut={() => setPhase({ name: "signed-out", saved: server })}
    />
  );
}
